// Public quote service — no authentication
// Routes are mounted at /q/:token (not /api/v1) for shorter, QR-friendly URLs.

import { PUBLIC_API_BASE, publicGet, publicPost } from "./public-fetch"

// ---- Types ----

export interface PublicQuoteLine {
  description: string
  detailed_description: string | null
  quantity: string | number
  unit: string | null
  unit_price: string | number
  total_amount: string | number
}

export interface PublicQuoteResponse {
  quote_number: string
  reference: string | null
  status_code: string
  status_label: string | null
  issue_date: string
  validity_date: string
  is_expired: boolean
  tenant: {
    name: string
    logo_url: string | null
  }
  client: {
    account_name: string | null
    contact_name: string | null
    contact_email: string | null
    billing_address: string | null
    billing_postal_code: string | null
    billing_city: string | null
  }
  lines: PublicQuoteLine[]
  subtotal_amount: string | number
  discount_amount: string | number
  tax_rate: string | number
  tax_amount: string | number
  total_amount: string | number
  currency: string
  notes: string | null
  special_conditions: string | null
  accepted_at: string | null
  accepted_by_name: string | null
  rejected_at: string | null
  rejected_by_name: string | null
  needs_assessment_enabled: boolean
  needs_form_url: string | null
  program_ids: string[]
}

export interface PublicNeedsForm {
  quote_number: string
  tenant_name: string
  programs: { id: string; code: string; title: string }[]
  questions: {
    id: string
    question_text: string
    help_text: string | null
    question_type_code: string
    is_required: boolean
    sort_order: number
    options: { label: string; value: string; sort_order: number }[]
    min_value: number | null
    max_value: number | null
  }[]
  gdpr: {
    controller_name: string | null
    dpo_email: string | null
    purpose: string
    retention: string
    rights: string
  }
  already_submitted_count: number
}

// ---- Endpoints ----

const PUBLIC_API_PREFIX = "/api/v1/public/quotes"

export function getPublicQuote(token: string) {
  return publicGet<PublicQuoteResponse>(`${PUBLIC_API_PREFIX}/${token}`)
}

export function acceptPublicQuote(
  token: string,
  payload: { name: string; email: string; cgu_accepted: boolean },
) {
  return publicPost<{ status: string; accepted_at: string }>(
    `${PUBLIC_API_PREFIX}/${token}/accept`,
    payload,
  )
}

export function rejectPublicQuote(
  token: string,
  payload: { name: string; email: string; reason?: string },
) {
  return publicPost<{ status: string; rejected_at: string }>(
    `${PUBLIC_API_PREFIX}/${token}/reject`,
    payload,
  )
}

export function getPublicNeedsForm(token: string) {
  return publicGet<PublicNeedsForm>(`${PUBLIC_API_PREFIX}/${token}/needs`)
}

export function submitPublicNeeds(
  token: string,
  payload: {
    email: string
    name?: string
    program_id?: string
    answers: Record<string, unknown>
    consent: boolean
  },
) {
  return publicPost<{ status: string; response_id: string; message: string }>(
    `${PUBLIC_API_PREFIX}/${token}/needs/submit`,
    payload,
  )
}

export function publicQuotePdfUrl(token: string): string {
  return `${PUBLIC_API_BASE}${PUBLIC_API_PREFIX}/${token}/pdf`
}

// ---- Avenant public (issue #19, Jambe A) ----
// Routes montées sous /api/v1/public/quote-amendments/:token. La SPA sert
// /qa/:token. 410 Gone quand l'avenant est résolu (accepté/refusé/annulé/expiré).

const PUBLIC_AMENDMENT_PREFIX = "/api/v1/public/quote-amendments"

export interface PublicAmendmentLine {
  change_type: "add" | "modify" | "remove"
  line_type: string
  description: string
  quantity: string | number
  unit: string | null
  unit_price: string | number
  line_delta_ht: string | number
}

export interface PublicQuoteAmendmentResponse {
  quote_number: string
  amendment_number: number
  status_code: string
  tenant_name: string
  tenant_logo_url: string | null
  contact_type: string | null
  /** Client particulier : compte du devis de type « individual ». */
  // comply-ignore: api-no-boolean-field-in-response — fait binaire (particulier ou non), dérivé d'une seule fonction SQL.
  is_individual: boolean
  base_total_ht: string | number
  total_amount_delta_ht: string | number
  total_amount_delta_ttc: string | number
  effective_total_ht: string | number
  validity_date: string | null
  is_expired: boolean
  notes: string | null
  lines: PublicAmendmentLine[]
  legal_mention: string
}

export function getPublicQuoteAmendment(token: string) {
  return publicGet<PublicQuoteAmendmentResponse>(`${PUBLIC_AMENDMENT_PREFIX}/${token}`)
}

export function acceptPublicQuoteAmendment(
  token: string,
  payload: { name: string; email: string; cgu_accepted: boolean },
) {
  return publicPost<{ status: string; signed: boolean; accepted_at: string }>(
    `${PUBLIC_AMENDMENT_PREFIX}/${token}/accept`,
    payload,
  )
}

export function rejectPublicQuoteAmendment(
  token: string,
  payload: { name: string; email: string; reason?: string },
) {
  return publicPost<{ status: string; rejected_at: string }>(
    `${PUBLIC_AMENDMENT_PREFIX}/${token}/reject`,
    payload,
  )
}
